import { useContext } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Divider, IconButton, Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AuthContext from "../../../context/AuthContext";
import { mtc } from "../../../theme/theme";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SolarPowerRoundedIcon from "@mui/icons-material/SolarPowerRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CellTowerRoundedIcon from "@mui/icons-material/CellTowerRounded";
import RouterRoundedIcon from "@mui/icons-material/RouterRounded";
import SettingsEthernetRoundedIcon from "@mui/icons-material/SettingsEthernetRounded";

const drawerWidth = 264;

const navItems = [
  { label: "Dashboard", path: "/", icon: <DashboardRoundedIcon /> },
  { label: "Geyser Control", path: "/geyser", icon: <WaterDropRoundedIcon /> },
  { label: "Recharge", path: "/recharge", icon: <AccountBalanceWalletRoundedIcon /> },
  { label: "Statistics", path: "/statistics", icon: <BarChartRoundedIcon /> },
  { label: "Net Metering", path: "/net-metering", icon: <SolarPowerRoundedIcon /> },
  { label: "Data Usage", path: "/data-usage", icon: <CellTowerRoundedIcon /> },
  { label: "Network Products", path: "/network-products", icon: <RouterRoundedIcon />, badge: "New" },
  { label: "Router", path: "/router", icon: <SettingsEthernetRoundedIcon />, badge: "New" },
  { divider: true },
  { label: "Profile", path: "/profile", icon: <PersonRoundedIcon /> },
  { label: "Settings", path: "/settings", icon: <SettingsRoundedIcon /> },
];

function Sidebar({ window, mobileOpen, handleDrawerToggle }) {
  const theme = useTheme();
  const location = useLocation();
  const { userInfo, HandleLogOut } = useContext(AuthContext);
  const isDark = theme.palette.mode === "dark";

  const container = window !== undefined ? () => window().document.body : undefined;

  const userName = userInfo
    ? `${userInfo.FirstName || ""} ${userInfo.LastName || ""}`.trim() || userInfo.Email
    : "User";
  const userDRN = userInfo?.DRN || "";

  const sidebarBg = isDark ? "#0a0f1e" : "#ffffff";
  const activeBg = isDark ? "rgba(31,99,242,0.16)" : "rgba(13,79,219,0.08)";
  const activeColor = isDark ? mtc.blue[300] : mtc.blue[600];
  const textColor = isDark ? "#cbd5e1" : "#334155";
  const hoverBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)";

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", py: 2 }}>
      <Box sx={{ px: 2.5, mb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2.5, display: "flex", alignItems: "center", justifyContent: "center",
            background: isDark ? "rgba(31,99,242,0.12)" : "rgba(13,79,219,0.06)",
            border: `1px solid ${isDark ? "rgba(31,99,242,0.25)" : "rgba(13,79,219,0.15)"}`,
          }}>
            <img src="/meter-logo-small.png" alt="GRIDx" style={{ width: 30, height: "auto" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1, color: isDark ? "#f1f5f9" : "#0f172a", letterSpacing: "-0.01em" }}>
              GRIDx
            </Typography>
            <Typography sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.06em", color: activeColor, textTransform: "uppercase" }}>
              Client Portal
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ display: { sm: "none" }, color: textColor }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, my: 2 }}>
        <Box sx={{
          p: 1.5, borderRadius: 2,
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
          display: "flex", alignItems: "center", gap: 1.5,
        }}>
          <Avatar sx={{
            width: 38, height: 38,
            bgcolor: isDark ? "#1e3a5f" : "#dbeafe",
            color: isDark ? "#4b86ff" : "#0d4fdb",
            fontSize: 15, fontWeight: 600,
          }}>
            {userName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{
              fontSize: 13, fontWeight: 600, color: isDark ? "#e2e8f0" : "#1e293b",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {userName}
            </Typography>
            {userDRN && (
              <Typography sx={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8", fontFamily: "monospace" }}>
                {userDRN}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1.5 }}>
        {navItems.map((item, idx) => {
          if (item.divider) return <Divider key={idx} sx={{ my: 1.5, borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.08)" }} />;
          const isActive = location.pathname === item.path;
          const isDisabled = item.disabled;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.3 }}>
              <ListItemButton
                component={isDisabled ? "div" : RouterLink}
                to={isDisabled ? undefined : item.path}
                onClick={(e) => {
                  if (isDisabled) { e.preventDefault(); return; }
                  if (mobileOpen) handleDrawerToggle();
                }}
                sx={{
                  borderRadius: 2, py: 1, px: 1.5,
                  bgcolor: isActive ? activeBg : "transparent",
                  "&:hover": { bgcolor: isDisabled ? "transparent" : (isActive ? activeBg : hoverBg) },
                  transition: "all 0.15s ease",
                  opacity: isDisabled ? 0.45 : 1,
                  cursor: isDisabled ? "default" : "pointer",
                }}
              >
                <ListItemIcon sx={{
                  minWidth: 36, color: isActive ? activeColor : textColor,
                  "& .MuiSvgIcon-root": { fontSize: 20 },
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 13.5, fontWeight: isActive ? 600 : 400,
                    color: isActive ? activeColor : textColor,
                  }}
                />
                {item.badge && !isActive && (
                  <Chip
                    label={item.badge}
                    size="small"
                    sx={{
                      height: 18, fontSize: 9.5, fontWeight: 700, ml: 1,
                      bgcolor: isDark ? "rgba(16,185,129,0.15)" : "rgba(5,150,105,0.1)",
                      color: isDark ? "#34d399" : "#059669",
                    }}
                  />
                )}
                {isActive && (
                  <Box sx={{
                    width: 4, height: 20, borderRadius: 2,
                    background: mtc.gradient, ml: 1,
                  }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ px: 1.5, pb: 1 }}>
        <ListItemButton
          onClick={HandleLogOut}
          sx={{
            borderRadius: 2, py: 1, px: 1.5,
            "&:hover": { bgcolor: "rgba(239,68,68,0.08)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: "#ef4444", "& .MuiSvgIcon-root": { fontSize: 20 } }}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText
            primary="Log Out"
            primaryTypographyProps={{ fontSize: 13.5, color: "#ef4444", fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
    >
      <Drawer
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", sm: "none" },
          "& .MuiDrawer-paper": {
            width: drawerWidth, boxSizing: "border-box",
            bgcolor: sidebarBg, borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", sm: "block" },
          "& .MuiDrawer-paper": {
            width: drawerWidth, boxSizing: "border-box",
            bgcolor: sidebarBg, borderRight: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

Sidebar.propTypes = {
  window: PropTypes.func,
  mobileOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
};

export default Sidebar;
